import React, { useState, useRef, useCallback } from 'react';
import { settingsLinks, useSettingsSections } from '@/modules/settings/hooks';
import { NavLink, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  LayoutDashboard,
  TrendingUp,
  Megaphone,
  Target,
  Settings,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Lock,
  Printer,
  Users,
  PhoneCall,
  ShieldCheck,
  FileCheck2,
  ListChecks,
  ClipboardCheck,
  Send,
} from 'lucide-react';
import { useAdsState } from '@/modules/campaigns/hooks/useAdsState';
import { useLatestAudit } from '@/modules/seo/hooks/useAudit';
import { useFeatureFlag } from '@/shared/hooks/useFeatureFlag';
import { useVertical } from '@/shared/hooks/useVertical';
import { useAuthContext } from '@/shared/context/useAuthContext';
import styles from './Sidebar.module.css';

/** localStorage key for a nav section's open state, scoped to the viewing user
 *  (not the practice) so each person keeps their own expand/collapse choices. */
function navPrefKey(userId: string | undefined, label: string): string {
  return `pn.nav.open.${userId ?? 'anon'}.${label}`;
}

/**
 * Open-state that remembers itself in localStorage, per user. Falls back to
 * `fallback` (the section's static default) when nothing is stored, and degrades
 * to plain state when storage is unavailable (private window, blocked, etc.).
 */
function usePersistentOpen(
  storageKey: string,
  fallback: boolean,
): [boolean, (updater: boolean | ((o: boolean) => boolean)) => void] {
  const [open, setOpenState] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw === 'true') return true;
      if (raw === 'false') return false;
    } catch {
      /* storage unavailable — use the fallback */
    }
    return fallback;
  });
  const setOpen = useCallback(
    (updater: boolean | ((o: boolean) => boolean)) => {
      setOpenState((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        try {
          localStorage.setItem(storageKey, next ? 'true' : 'false');
        } catch {
          /* ignore write failures */
        }
        return next;
      });
    },
    [storageKey],
  );
  return [open, setOpen];
}

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

interface NavSection {
  type: 'section';
  label: string;
  icon: React.ElementType;
  children: NavChild[];
  defaultOpen?: boolean;
}

interface NavChild {
  label: string;
  path: string;
  tag?: string;
  locked?: boolean;
  lockedReason?: string;
  /** Extra prefix that should also mark this link as active (e.g. detail pages). */
  alsoMatchPrefix?: string;
}

interface NavLink_ {
  type: 'link';
  label: string;
  path: string;
  icon: React.ElementType;
}

interface NavDivider {
  type: 'divider';
  label: string;
}

type NavItem = NavSection | NavLink_ | NavDivider;

const MAIN_NAV: NavItem[] = [
  {
    type: 'section',
    label: 'Dashboard',
    icon: LayoutDashboard,
    children: [{ label: 'Overview', path: '/' }],
  },
  {
    type: 'section',
    label: 'Strategy',
    icon: Target,
    children: [
      { label: 'Overview', path: '/strategy' },
      {
        label: 'Strategy Builder',
        path: '/strategy/builder',
        alsoMatchPrefix: '/strategy/builder',
      },
      { label: 'Brand & Positioning', path: '/strategy/brand-positioning' },
      { label: 'Competitors', path: '/strategy/builder/competition' },
      { label: 'Assets', path: '/strategy/assets' },
    ],
  },
  {
    type: 'section',
    label: 'Ad Campaigns',
    icon: Megaphone,
    children: [
      { label: 'Overview', path: '/campaigns' },
      { label: 'Campaigns', path: '/campaigns/list', alsoMatchPrefix: '/campaigns/' },
      { label: 'Performance', path: '/campaigns/performance' },
      { label: 'Activity', path: '/campaigns/activity' },
    ],
  },
  {
    type: 'section',
    label: 'Online Visibility',
    icon: TrendingUp,
    children: [
      { label: 'Overview', path: '/seo' },
      { label: 'Content', path: '/seo/content' },
      { label: 'Performance', path: '/seo/performance' },
      { label: 'Website Health', path: '/seo/site-health' },
      { label: 'Reviews', path: '/seo/reviews' },
      { label: 'Site Audit', path: '/seo/audit' },
    ],
  },
];

/** Settings; its pages are the practice's (settingsLinks): only those for modules switched on for it. */
const SETTINGS_BASE = { type: 'section', label: 'Settings', icon: Settings } as const;

// ~32px header + ~34px per item + 12px padding
function estimateFlyoutHeight(childCount: number) {
  return 32 + childCount * 34 + 12;
}

interface FlyoutState {
  label: string;
  children: NavChild[];
  top?: number;
  bottom?: number;
}

function buildFlyoutPosition(
  rect: DOMRect,
  childCount: number,
): Pick<FlyoutState, 'top' | 'bottom'> {
  const height = estimateFlyoutHeight(childCount);
  const spaceBelow = window.innerHeight - rect.top - 8;
  if (height > spaceBelow) {
    return { bottom: window.innerHeight - rect.bottom };
  }
  return { top: rect.top };
}

function CollapsedFlyout({
  flyout,
  onMouseEnter,
  onMouseLeave,
}: {
  flyout: FlyoutState;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  const location = useLocation();
  const siblings = flyout.children;
  return (
    <div
      className={styles.flyout}
      style={{ top: flyout.top, bottom: flyout.bottom }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className={styles.flyoutLabel}>{flyout.label}</div>
      {siblings.map((child) =>
        child.locked ? (
          <div
            key={child.path}
            className={clsx(styles.flyoutItem, styles.flyoutItemLocked)}
            title={child.lockedReason ?? 'Locked'}
          >
            <span>{child.label}</span>
            <Lock size={11} className={styles.lockIcon} />
          </div>
        ) : (
          <NavLink
            key={child.path}
            to={child.path}
            end
            className={({ isActive }) => {
              const prefixMatch =
                !isActive &&
                !!child.alsoMatchPrefix &&
                location.pathname.startsWith(child.alsoMatchPrefix) &&
                !siblings.some((s) => s !== child && location.pathname === s.path);
              return clsx(styles.flyoutItem, (isActive || prefixMatch) && styles.flyoutItemActive);
            }}
          >
            {child.label}
            {child.tag && <span className={styles.navTag}>{child.tag}</span>}
          </NavLink>
        ),
      )}
    </div>
  );
}

function NavSectionItem({
  item,
  collapsed,
  onFlyoutEnter,
  onFlyoutLeave,
  defaultOpen = false,
  storageScope,
}: {
  item: NavSection;
  collapsed: boolean;
  onFlyoutEnter: (state: FlyoutState) => void;
  onFlyoutLeave: () => void;
  defaultOpen?: boolean;
  /** The viewing user's id — scopes the remembered open state to this user. */
  storageScope?: string;
}) {
  const location = useLocation();
  const isActive = item.children.some((c) =>
    c.path === '/' ? location.pathname === '/' : location.pathname.startsWith(c.path),
  );
  const Icon = item.icon;
  const itemRef = useRef<HTMLDivElement>(null);
  // Remembered per user: once expanded (or collapsed), it stays that way until
  // the user changes it again. New sections fall back to their static default.
  const [open, setOpen] = usePersistentOpen(navPrefKey(storageScope, item.label), defaultOpen);

  if (collapsed) {
    return (
      <div
        ref={itemRef}
        className={styles.navGroup}
        onMouseEnter={() => {
          const rect = itemRef.current?.getBoundingClientRect();
          if (rect)
            onFlyoutEnter({
              label: item.label,
              children: item.children,
              ...buildFlyoutPosition(rect, item.children.length),
            });
        }}
        onMouseLeave={onFlyoutLeave}
      >
        <div className={clsx(styles.navItem, isActive && styles.navItemActive)}>
          <Icon size={16} className={styles.navIcon} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.navGroup}>
      <button
        className={clsx(styles.navItem, styles.settingsToggle, isActive && styles.navItemActive)}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon size={16} className={styles.navIcon} />
        <span className={styles.navLabel}>{item.label}</span>
        <ChevronDown
          size={13}
          className={clsx(styles.settingsChevron, open && styles.settingsChevronOpen)}
        />
      </button>
      <div className={clsx(styles.subNav, open && styles.subNavOpen)}>
        <div className={styles.subNavInner}>
          {item.children.map((child) =>
            child.locked ? (
              <div
                key={child.path}
                className={clsx(styles.subNavItem, styles.subNavItemLocked)}
                title={child.lockedReason ?? 'Locked'}
              >
                <span>{child.label}</span>
                <Lock size={12} className={styles.lockIcon} />
              </div>
            ) : (
              <NavLink
                key={child.path}
                to={child.path}
                end
                className={({ isActive }) => {
                  const prefixMatch =
                    !isActive &&
                    !!child.alsoMatchPrefix &&
                    location.pathname.startsWith(child.alsoMatchPrefix) &&
                    !item.children.some((s) => s !== child && location.pathname === s.path);
                  return clsx(styles.subNavItem, (isActive || prefixMatch) && styles.subActive);
                }}
              >
                {child.label}
                {child.tag && <span className={styles.navTag}>{child.tag}</span>}
              </NavLink>
            ),
          )}
        </div>
      </div>
    </div>
  );
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const location = useLocation();
  const { user } = useAuthContext();
  const storageScope = user?.id;
  const settingsSections = useSettingsSections();
  const SETTINGS_SECTION: NavSection = {
    ...SETTINGS_BASE,
    children: settingsLinks(settingsSections),
  };
  const settingsActive = SETTINGS_SECTION.children.some((l) =>
    location.pathname.startsWith(l.path),
  );
  const { navLocked: adsNavLocked } = useAdsState();
  const { data: latestAudit, isSuccess: auditLoaded } = useLatestAudit();
  const seoLocked = auditLoaded && !latestAudit;

  const channelMixEnabled = useFeatureFlag('channel-mix');
  const printMailEnabled = useFeatureFlag('print-mail');
  // Community & Referrals (Joe): the nav item exists only while the practice flag is on; the server
  // 404s every Community route without it either way.
  const communityEnabled = useFeatureFlag('community');
  const callsEnabled = useFeatureFlag('calls');
  // Safety content is dental-clinical (OSHA BBP, waterlines). Verticals without a
  // training pack hide the module rather than show another industry's compliance.
  const { vertical } = useVertical();
  const safetyTrainingEnabled = useFeatureFlag('safety-training') && vertical.modules.safety;
  const insuranceEnabled = useFeatureFlag('insurance-verification');
  const legacyInsuranceEnabled = useFeatureFlag('insurance-verification-legacy');
  // Patient Referrals: the nav item exists only while the practice flag is on; the server 404s
  // every /referrals route without it either way.
  const referralsEnabled = useFeatureFlag('patient-referrals');

  // Build nav in display order, inserting channel dividers and applying lock states.
  // Strategy comes first (before paid channels), then PAID, then ORGANIC & OFFLINE.
  // Online Visibility: locked until an AuditResult row exists for the location.
  // Ad Campaigns: locked until the module has something to act on (see useAdsState navLocked).
  // The section Overview is always available so the user has a path to unlock.
  const navWithLocks: NavItem[] = [];

  // Dashboard
  navWithLocks.push(MAIN_NAV[0]);

  // Keep past work accessible when the assignment pauses. API location checks still apply.
  navWithLocks.push({ type: 'link', label: 'Tasks', path: '/tasks', icon: ListChecks });

  // Strategy (with optional brand kit / channel mix children)
  {
    const item = MAIN_NAV[1] as NavSection;
    const children = item.children.map((child) => {
      if (child.path !== '/strategy/builder/competition') return child;
      return { ...child, locked: seoLocked, lockedReason: 'Locked — run your SEO Audit to unlock' };
    });
    if (channelMixEnabled) {
      children.splice(
        children.findIndex((c) => c.path === '/strategy/builder/competition'),
        0,
        { label: 'Channel Mix', path: '/strategy/channel-mix' },
      );
    }
    navWithLocks.push({ ...item, children });
  }

  // PAID
  navWithLocks.push({ type: 'divider', label: 'Paid' });
  {
    const item = MAIN_NAV[2] as NavSection;
    navWithLocks.push({
      ...item,
      children: item.children.map((child) => {
        if (child.path === '/campaigns') return child;
        return {
          ...child,
          locked: adsNavLocked,
          lockedReason: 'Locked — connect an ad account to unlock',
        };
      }),
    });
  }

  // ORGANIC & OFFLINE
  navWithLocks.push({ type: 'divider', label: 'Organic & Offline' });
  {
    const item = MAIN_NAV[3] as NavSection;
    navWithLocks.push({
      ...item,
      children: item.children.map((child) => {
        if (child.path === '/seo') return child;
        return {
          ...child,
          locked: seoLocked,
          lockedReason: 'Locked — run your SEO Audit to unlock',
        };
      }),
    });
  }

  // Community sits under the Organic & Offline divider (no divider of its own).
  if (communityEnabled) {
    navWithLocks.push({
      type: 'section',
      label: 'Community',
      icon: Users,
      children: [{ label: 'Picks & network', path: '/community' }],
    });
  }

  if (printMailEnabled) {
    navWithLocks.push({
      type: 'section',
      label: 'Print & Mail',
      icon: Printer,
      children: [
        { label: 'Mailers', path: '/print-mail/mailers' },
        { label: 'Letterhead & Print', path: '/print-mail/letterhead' },
      ],
    });
  }

  // OPERATIONS (renamed from Convert; also holds the former Back Office items)
  if (callsEnabled || safetyTrainingEnabled || insuranceEnabled || referralsEnabled) {
    navWithLocks.push({ type: 'divider', label: 'Operations' });
  }
  if (referralsEnabled) {
    navWithLocks.push({
      type: 'section',
      label: 'Referrals',
      icon: Send,
      children: [
        { label: 'Referrals', path: '/referrals', alsoMatchPrefix: '/referrals/' },
        { label: 'Create referral', path: '/referrals/new' },
        { label: 'Setup your form', path: '/referrals/settings' },
      ],
    });
  }
  if (callsEnabled) {
    navWithLocks.push({
      type: 'section',
      label: 'Calls',
      icon: PhoneCall,
      children: [
        { label: 'Call Log', path: '/calls/log' },
        { label: 'Agents', path: '/calls/agents' },
        { label: 'Sources', path: '/calls/sources' },
      ],
    });
  }
  if (safetyTrainingEnabled) {
    navWithLocks.push({
      type: 'section',
      label: 'Safety & Training',
      icon: ShieldCheck,
      children: [
        { label: 'Overview', path: '/safety' },
        { label: 'Staff Training', path: '/safety/training', alsoMatchPrefix: '/safety/training' },
        { label: 'Equipment Logs', path: '/safety/equipment' },
        { label: 'Records', path: '/safety/records', alsoMatchPrefix: '/safety/records' },
      ],
    });
  }

  if (insuranceEnabled) {
    // Worklist uses an exact match (NavLink `end`) so it isn't lit on the sibling
    // pages; alsoMatchPrefix keeps it lit on verification detail pages.
    navWithLocks.push({
      type: 'section',
      label: 'Insurance Verification',
      icon: ClipboardCheck,
      children: [
        {
          label: 'Worklist',
          path: '/insurance-verification',
          alsoMatchPrefix: '/insurance-verification/verifications',
        },
        { label: 'Import schedule', path: '/insurance-verification/import' },
        { label: 'Templates', path: '/insurance-verification/templates' },
      ],
    });
  }
  // The older call-based screens, kept for testing — hidden unless switched on per practice.
  if (insuranceEnabled && legacyInsuranceEnabled) {
    navWithLocks.push({
      type: 'section',
      label: 'OLD Insurance Verification',
      icon: FileCheck2,
      children: [
        { label: 'Practice Credentials', path: '/insurance/credentials' },
        { label: 'Payers', path: '/insurance/payers' },
      ],
    });
  }

  const [settingsOpen, setSettingsOpen] = usePersistentOpen(
    navPrefKey(storageScope, 'Settings'),
    false,
  );
  const [flyout, setFlyout] = useState<FlyoutState | null>(null);
  const flyoutTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleFlyoutEnter = (state: FlyoutState) => {
    if (flyoutTimeoutRef.current) clearTimeout(flyoutTimeoutRef.current);
    setFlyout(state);
  };

  const handleFlyoutLeave = () => {
    flyoutTimeoutRef.current = setTimeout(() => setFlyout(null), 100);
  };

  const settingsItemRef = useRef<HTMLDivElement>(null);

  return (
    <nav className={clsx(styles.sidebar, collapsed && styles.collapsed)}>
      <div className={styles.header}>
        {!collapsed && (
          <div className={styles.logo}>
            <span className={styles.logoText}>PracticeNova</span>
          </div>
        )}
        <button className={styles.toggleBtn} onClick={onToggle} aria-label="Toggle sidebar">
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      <div className={styles.nav}>
        <div className={styles.navMain}>
          {navWithLocks.map((item) => {
            if (item.type === 'divider') {
              return collapsed ? (
                <div key={item.label} className={styles.navSectionDivider} />
              ) : (
                <div key={item.label} className={styles.navSectionLabel}>
                  {item.label}
                </div>
              );
            }
            if (item.type === 'link') {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end
                  className={({ isActive }) =>
                    clsx(styles.navItem, isActive && styles.navItemActive)
                  }
                >
                  <Icon size={16} className={styles.navIcon} />
                  {!collapsed && <span className={styles.navLabel}>{item.label}</span>}
                </NavLink>
              );
            }
            return (
              <NavSectionItem
                key={item.label}
                item={item}
                collapsed={collapsed}
                defaultOpen={item.defaultOpen}
                storageScope={storageScope}
                onFlyoutEnter={handleFlyoutEnter}
                onFlyoutLeave={handleFlyoutLeave}
              />
            );
          })}
        </div>

        <div className={styles.navBottom}>
          <div className={styles.navDivider} />
          {collapsed ? (
            <div
              ref={settingsItemRef}
              className={styles.navGroup}
              onMouseEnter={() => {
                const rect = settingsItemRef.current?.getBoundingClientRect();
                if (rect)
                  handleFlyoutEnter({
                    label: SETTINGS_SECTION.label,
                    children: SETTINGS_SECTION.children,
                    ...buildFlyoutPosition(rect, SETTINGS_SECTION.children.length),
                  });
              }}
              onMouseLeave={handleFlyoutLeave}
            >
              <div className={clsx(styles.navItem, settingsActive && styles.navItemActive)}>
                <Settings size={16} className={styles.navIcon} />
              </div>
            </div>
          ) : (
            <div className={styles.navGroup}>
              <button
                className={clsx(
                  styles.navItem,
                  styles.settingsToggle,
                  settingsActive && styles.navItemActive,
                )}
                onClick={() => setSettingsOpen((o) => !o)}
              >
                <Settings size={16} className={styles.navIcon} />
                <span className={styles.navLabel}>Settings</span>
                <ChevronDown
                  size={13}
                  className={clsx(
                    styles.settingsChevron,
                    settingsOpen && styles.settingsChevronOpen,
                  )}
                />
              </button>
              <div className={clsx(styles.subNav, settingsOpen && styles.subNavOpen)}>
                <div className={styles.subNavInner}>
                  {SETTINGS_SECTION.children.map((link) => (
                    <NavLink
                      key={link.path}
                      to={link.path}
                      className={({ isActive }) =>
                        clsx(styles.subNavItem, isActive && styles.subActive)
                      }
                    >
                      {link.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Flyout for collapsed mode */}
      {collapsed && flyout && (
        <CollapsedFlyout
          flyout={flyout}
          onMouseEnter={() => handleFlyoutEnter(flyout)}
          onMouseLeave={handleFlyoutLeave}
        />
      )}
    </nav>
  );
}
